/**
 * Cliente PostgREST
 * 
 * Wrapper sobre o ApiClient que fornece uma interface simplificada
 * para operações PostgREST com suporte a filtros, ordenação, paginação e RPC.
 */

import { postgrestClient as apiClient } from "../../config/api";

/**
 * Converte filtros de objeto para query string do PostgREST
 * @param {Object} filters - Ex: { nucleo_id: "eq.123", nome: "ilike.*test*", data: "gte.2026-01-01,lte.2026-02-01" }
 * @returns {string} Query string formatada
 */
const buildFilters = (filters = {}) => {
  const params = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      let finalKey = key;
      let operator = 'eq';

      // Detecta operadores especiais nos nomes dos campos
      if (key.endsWith('_gte')) {
        finalKey = key.slice(0, -4); // Remove '_gte'
        operator = 'gte';
      } else if (key.endsWith('_lte')) {
        finalKey = key.slice(0, -4); // Remove '_lte'
        operator = 'lte';
      } else if (key.endsWith('_gt')) {
        finalKey = key.slice(0, -3); // Remove '_gt'
        operator = 'gt';
      } else if (key.endsWith('_lt')) {
        finalKey = key.slice(0, -3); // Remove '_lt'
        operator = 'lt';
      } else if (key.endsWith('_neq')) {
        finalKey = key.slice(0, -4); // Remove '_neq'
        operator = 'neq';
      } else if (key.endsWith('_like')) {
        finalKey = key.slice(0, -5); // Remove '_like'
        operator = 'like';
      } else if (key.endsWith('_ilike')) {
        finalKey = key.slice(0, -6); // Remove '_ilike'
        operator = 'ilike';
      }

      // Se o valor contém múltiplos operadores separados por vírgula (ex: "gte.2026-01-01,lte.2026-02-01")
      // Mas NÃO divide se for um operador 'in.' que já contém vírgulas nos valores
      if (typeof value === "string" && value.includes(",") && !value.startsWith("in.(")) {
        // Divide os operadores e cria parâmetros separados para o mesmo campo
        const operators = value.split(",");
        operators.forEach(operator => {
          params.push(`${finalKey}=${encodeURIComponent(operator.trim())}`);
        });
      } else {
        // Se o valor já contém o operador (ex: "eq.123"), usa diretamente
        // Caso contrário, usa o operador detectado
        const filterValue = typeof value === "string" && value.includes(".")
          ? value
          : `${operator}.${value}`;
        params.push(`${finalKey}=${encodeURIComponent(filterValue)}`);
      }
    }
  });

  return params.join("&");
};

/**
 * Constrói query string completa com filtros, ordenação, paginação
 */
const buildQueryString = (options = {}) => {
  const { filters = {}, order, limit, offset, select, includeCount, ...otherFilters } = options;
  const params = [];

  // Adiciona select (deve vir primeiro no PostgREST)
  if (select) {
    params.push(`select=${encodeURIComponent(select)}`);
  }

  // Combina filtros da propriedade 'filters' com filtros diretos
  const allFilters = { ...filters, ...otherFilters };

  // Adiciona filtros
  const filterParams = buildFilters(allFilters);
  if (filterParams) {
    params.push(filterParams);
  }
  
  // Adiciona ordenação
  if (order) {
    params.push(`order=${encodeURIComponent(order)}`);
  }
  
  // Adiciona limite
  if (limit !== undefined && limit !== null) {
    params.push(`limit=${limit}`);
  }
  
  // Adiciona offset
  if (offset !== undefined && offset !== null) {
    params.push(`offset=${offset}`);
  }
  
  // NOTA: count=exact deve ser enviado como header HTTP Prefer, não como query param
  
  return params.length > 0 ? `?${params.join("&")}` : "";
};

/**
 * Cliente PostgREST com interface simplificada
 */
export const postgrestClient = {
  /**
   * GET - Busca recursos
   * @param {string} endpoint - Ex: "alunos", "turmas", etc.
   * @param {Object} options - { filters, order, limit, offset, includeCount, select }
   * @returns {Promise<Array|Object>}
   */
  async get(endpoint, options = {}) {
    const { includeCount = false } = options;
    const queryString = buildQueryString(options);
    const url = queryString ? `${endpoint}${queryString}` : endpoint;
    
    // Prepara headers: se includeCount for true, adiciona header Prefer
    // PostgREST requer count como header Prefer, não como query param
    // O header Prefer padrão (return=representation) será mantido pelo ApiClient
    const headers = {};
    if (includeCount) {
      // PostgREST suporta múltiplos valores no header Prefer separados por vírgula
      // Combinamos com o valor padrão (return=representation)
      headers.Prefer = "return=representation,count=exact";
    }
    
    // Usa request diretamente para poder passar headers customizados
    // O método request já adiciona o baseURL automaticamente
    const response = await apiClient.request(url, {
      method: "GET",
      headers,
    });
    
    // Se includeCount foi solicitado, PostgREST retorna count no header Content-Range
    // Formato: "0-9/100" onde 100 é o total
    if (includeCount) {
      // Por enquanto, retornamos o array como data
      // TODO: Extrair count do header Content-Range quando disponível
      return {
        data: Array.isArray(response) ? response : response ? [response] : [],
        total: Array.isArray(response) ? response.length : response ? 1 : 0,
      };
    }
    
    return response;
  },

  /**
   * POST - Cria recurso
   * @param {string} endpoint - Ex: "alunos", "turmas", etc.
   * @param {Object} data - Dados do recurso
   * @returns {Promise<Object|Array>}
   */
  async post(endpoint, data) {
    return apiClient.post(endpoint, data);
  },

  /**
   * PATCH - Atualiza recurso
   * @param {string} endpoint - Ex: "alunos", "turmas", etc.
   * @param {Object} filters - Filtros para identificar o recurso (ex: { id: "eq.123" })
   * @param {Object} data - Dados a atualizar
   * @returns {Promise<Object|Array>}
   */
  async patch(endpoint, filters, data) {
    // Constrói query string com filtros
    const filterParams = buildFilters(filters);
    const url = filterParams ? `${endpoint}?${filterParams}` : endpoint;
    
    return apiClient.patch(url, data);
  },

  /**
   * DELETE - Remove recurso
   * @param {string} endpoint - Ex: "alunos", "turmas", etc.
   * @param {Object} filters - Filtros para identificar o recurso (ex: { id: "eq.123" })
   * @returns {Promise<null>}
   */
  async delete(endpoint, filters) {
    // Constrói query string com filtros
    const filterParams = buildFilters(filters);
    const url = filterParams ? `${endpoint}?${filterParams}` : endpoint;
    
    return apiClient.delete(url);
  },

  /**
   * RPC - Chama função RPC do PostgREST
   * @param {string} functionName - Nome da função RPC
   * @param {Object} params - Parâmetros da função
   * @returns {Promise<any>}
   */
  async rpc(functionName, params = {}) {
    // PostgREST RPC usa POST para /rpc/nome_funcao
    const endpoint = `rpc/${functionName}`;
    return apiClient.post(endpoint, params);
  },
};

export default postgrestClient;
